import { useEffect, useMemo, useState } from "react";
import { InfoHint } from "../components/InfoHint";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { BarChart3, ClipboardList, Euro, Clock, Building2, FileDown } from "lucide-react";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const COLORS = ["#7A193D", "#C98AA0", "#B45309", "#0F766E", "#1D4ED8", "#9333EA", "#DC2626", "#65A30D", "#EA580C", "#475569"];
const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const fmtMese = (ym) => { const [y, m] = ym.split("-"); return `${MESI[parseInt(m, 10) - 1]} ${y.slice(2)}`; };
const fmtEuro = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Statistiche() {
  const [s, setS] = useState(null);
  // Iter123k186 · filtro per condominio (dropdown sopra il grafico a torta) + export PDF
  const [condomini, setCondomini] = useState([]);
  const [condominioId, setCondominioId] = useState("");
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    api.get("/condomini?limit=10000").then((r) => {
      const list = r.data.items || r.data || [];
      setCondomini(list.map((c) => ({ id: c.id, nome: c.nome })).sort((a, b) => a.nome.localeCompare(b.nome)));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = condominioId ? `?condominio_id=${encodeURIComponent(condominioId)}` : "";
    api.get(`/stats${qs}`).then((r) => setS(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [condominioId]);

  const condominioNome = useMemo(() => {
    if (!condominioId) return "";
    return (condomini.find((c) => c.id === condominioId) || {}).nome || "";
  }, [condominioId, condomini]);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const qs = condominioId ? `?condominio_id=${encodeURIComponent(condominioId)}` : "";
      const r = await api.get(`/stats/pdf${qs}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const safeNome = (condominioNome || "generale").toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 40);
      a.download = `statistiche_${safeNome}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // noop — la UI mantiene lo stato precedente
    } finally {
      setPdfBusy(false);
    }
  };

  if (!s) return <div className="text-lg">Caricamento...</div>;

  const cat = s.by_categoria.filter((c) => c.count > 0);
  const stato = s.by_stato.filter((c) => c.count > 0);
  const mese = s.by_mese.map((m) => ({ ...m, label: fmtMese(m.mese) }));

  return (
    <div className="max-w-7xl" data-testid="statistiche-page">
      <div className="flex items-center gap-2 mb-5">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3">
          <BarChart3 className="w-9 h-9 text-brand" /> Statistiche
        </h1>
        <InfoHint section="statistiche" />
      </div>

      {/* Iter123k186 · toolbar: dropdown condominio + download PDF */}
      <Card className="p-3 mb-4 flex flex-wrap items-center gap-3 border-2" data-testid="stat-toolbar">
        <label className="text-sm font-semibold text-slate-700">Condominio:</label>
        <select data-testid="stat-condominio-filter"
          value={condominioId}
          onChange={(e) => setCondominioId(e.target.value)}
          className="rounded-lg border-2 border-slate-200 bg-white px-3 py-2 text-sm min-w-[220px] focus:border-brand focus:outline-none">
          <option value="">Tutti i condomìni (dato aggregato)</option>
          {condomini.map((c) => (
            <option key={c.id} value={c.id}>{c.nome}</option>
          ))}
        </select>
        {loading && <span className="text-xs text-slate-500">Aggiornamento...</span>}
        <div className="ml-auto">
          <Button data-testid="stat-pdf-btn" onClick={downloadPdf} disabled={pdfBusy}
            className="bg-brand hover:bg-brand/90 text-white gap-2">
            <FileDown className="w-4 h-4" />
            {pdfBusy ? "Generazione..." : "Scheda PDF"}
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <Kpi icon={ClipboardList} label="Interventi totali" value={s.totale} color="bg-blue-100 text-brand" testid="stat-totale" />
        <Kpi icon={Euro} label="Costo totale" value={fmtEuro(s.costo_totale)} color="bg-green-100 text-green-700" testid="stat-costo" />
        <Kpi icon={Clock} label="Tempo medio chiusura" value={s.tempo_medio_chiusura != null ? `${s.tempo_medio_chiusura} gg` : "—"} color="bg-amber-100 text-amber-700" testid="stat-tempo" />
        <Kpi icon={Building2} label="Condomìni con interventi" value={s.num_condomini_attivi} color="bg-purple-100 text-purple-700" testid="stat-condomini" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        <Card className="p-4 border-2" data-testid="chart-categoria">
          <h3 className="font-display text-xl font-bold mb-1">Interventi per categoria</h3>
          {condominioNome && (
            <div className="text-xs text-brand font-semibold mb-3" data-testid="chart-filter-label">
              Filtro: {condominioNome}
            </div>
          )}
          {cat.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={cat} dataKey="count" nameKey="categoria" cx="50%" cy="50%" outerRadius={100}
                  label={(e) => `${e.categoria} (${e.count})`}>
                  {cat.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-4 border-2" data-testid="chart-stato">
          <h3 className="font-display text-xl font-bold mb-4">Interventi per stato</h3>
          {stato.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stato}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="stato" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#7A193D" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <Card className="p-4 border-2 mb-5" data-testid="chart-mese">
        <h3 className="font-display text-xl font-bold mb-4">Andamento mensile delle manutenzioni</h3>
        {mese.length === 0 ? <Empty /> : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={mese}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="#C98AA0" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      {!condominioId && (
      <Card className="p-4 border-2" data-testid="table-condomini">
        <h3 className="font-display text-xl font-bold mb-4">Dettaglio per condominio</h3>
        {s.per_condominio.length === 0 ? <Empty /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-base">
              <thead>
                <tr className="text-left text-sm font-bold uppercase tracking-wide text-slate-500 border-b">
                  <th className="py-2 pr-4">Condominio</th>
                  <th className="py-2 pr-4">Interventi</th>
                  <th className="py-2 pr-4">Aperte</th>
                  <th className="py-2 pr-4">Chiuse</th>
                  <th className="py-2 pr-4">Costo totale</th>
                  <th className="py-2 pr-4">Fornitori più usati</th>
                </tr>
              </thead>
              <tbody>
                {s.per_condominio.map((c, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`condominio-stat-${i}`}>
                    <td className="py-2 pr-4 font-semibold">{c.condominio_nome}</td>
                    <td className="py-2 pr-4">{c.n_interventi}</td>
                    <td className="py-2 pr-4">{c.n_aperte}</td>
                    <td className="py-2 pr-4">{c.n_chiuse}</td>
                    <td className="py-2 pr-4">{fmtEuro(c.costo_totale)}</td>
                    <td className="py-2 pr-4 text-sm text-slate-600">
                      {c.fornitori_top.length ? c.fornitori_top.map((f) => `${f.nome} (${f.interventi})`).join(", ") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color, testid }) {
  return (
    <Card data-testid={testid} className="p-4 border-2">
      <div className="flex items-center gap-4">
        <div className={`rounded-xl p-3 ${color}`}><Icon className="w-8 h-8" /></div>
        <div>
          <div className="text-3xl font-display font-extrabold">{value}</div>
          <div className="text-base font-semibold text-slate-600">{label}</div>
        </div>
      </div>
    </Card>
  );
}

function Empty() {
  return <div className="text-slate-500 text-base py-10 text-center">Nessun dato disponibile.</div>;
}
